import { Phone, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/Button';
import { WhatsAppIcon } from '../components/icons/WhatsAppIcon';
import { useSettings } from "../api/hooks/useSettings";
import { formatNumberWithCommas } from '../utils/formatters';

interface StickyActionBarProps {
    title: string;
    subtitle?: string;
    price?: string | number;
    type: string; // 'Unit' | 'Project' | 'Villa' etc.
    location?: string | null;
}

export const StickyActionBar = ({ title, subtitle, price, type, location }: StickyActionBarProps) => {
    const { t } = useTranslation();
    const { data: settings } = useSettings();

    const handleCall = () => {
        if (settings?.phone) {
            window.location.href = `tel:${settings.phone}`;
        }
    };

    const handleWhatsApp = () => {
        if (settings?.whatsapp) {
            let translatedType = type;
            const lowerType = type.toLowerCase();

            if (lowerType === 'project') {
                translatedType = t('common.project');
            } else if (lowerType === 'unit') {
                translatedType = t('common.unit');
            } else {
                // Try to translate property types (Apartment, Villa, Twin House -> twinhouse, etc.)
                // Normalize: remove spaces, hyphens, and make lowercase to match keys in en.json/ar.json
                const normalizedType = type.toLowerCase().replace(/[^a-z0-9]/g, '');
                const typeKey = `propertyConfig.types.${normalizedType}`;

                // key exists check is not reliable with all i18n backends, so we check if translation matches key
                // But here we used defaultValue as type. 
                // A better approach validation might be checking if the key exists in the resource bundle if possible,
                // but checking if translation !== key is a simple heuristic if we didn't pass defaultValue.
                // However, passing defaultValue is good UX.
                // Let's rely on the fact that if it returns the key (typeKey) it failed (if we don't pass default).
                // unique string as default to check if translation exists
                const fallback = '___FALLBACK___';
                const translated = t(typeKey, { defaultValue: fallback });

                if (translated !== fallback) {
                    translatedType = translated;
                }
            }

            const message = t('cta.whatsappMessage', { type: translatedType, title });
            const url = `https://wa.me/${settings.whatsapp.replace('+', '')}?text=${encodeURIComponent(message)}`;
            window.open(url, '_blank');
        }
    };

    if (!settings) return null; // Don't show if settings aren't loaded yet

    return (
        <div className="sticky top-[64px] z-40 bg-white border-b border-gray-100 shadow-sm transition-all duration-300 animate-fade-in-down">
            <div className="container mx-auto px-4 md:px-12 lg:px-24">
                <div className="flex items-center justify-between py-3 md:py-4">
                    {/* Left: Info */}
                    <div className="flex flex-col text-start">
                        <div className="flex items-center gap-2 mb-0.5">
                            {location && (
                                <div className="flex items-center gap-1 text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-wider">
                                    <MapPin className="w-3 h-3 text-secondary" />
                                    {location}
                                </div>
                            )}
                            {subtitle && (
                                <>
                                    <span className="text-slate-300">|</span>
                                    <span className="text-[10px] md:text-xs font-bold text-secondary uppercase tracking-wider">
                                        {subtitle}
                                    </span>
                                </>
                            )}
                        </div>
                        <h1 className="text-lg md:text-xl font-display font-bold text-secondary leading-none truncate max-w-[200px] md:max-w-md">
                            {title}
                        </h1>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 md:gap-4">
                        {price && (
                            <div className="hidden md:block text-right mr-4">
                                <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">{t('featuredUnits.price', 'Price')}</p>
                                <p className="text-lg font-bold text-secondary leading-none">
                                    {formatNumberWithCommas(price)} <span className="text-xs">{t('common.currency', 'EGP')}</span>
                                </p>
                            </div>
                        )}

                        <Button
                            variant="primary"
                            size="md"
                            className="bg-secondary hover:bg-secondary-highlight text-white w-10 h-10 p-0 md:w-auto md:px-6 md:py-2 md:h-auto rounded-none md:rounded-hesn flex items-center justify-center"
                            onClick={handleCall}
                        >
                            <Phone className="w-4 h-4 md:mr-2 fill-current" />
                            <span className="hidden md:inline">{t('project.callSales', 'Chat With Us')}</span>
                        </Button>

                        <Button
                            variant="outline"
                            size="md"
                            className="border-secondary text-secondary hover:bg-secondary hover:text-white w-10 h-10 p-0 md:w-auto md:px-6 md:py-2 md:h-auto rounded-none md:rounded-hesn flex items-center justify-center"
                            onClick={handleWhatsApp}
                        >
                            <WhatsAppIcon className="w-4 h-4 md:mr-2 fill-current" />
                            <span className="hidden md:inline">{t('cta.whatsapp', 'WHATSAPP')}</span>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StickyActionBar;
